import {
  Component,
  input,
  output,
  inject,
  signal,
  viewChild,
  TemplateRef,
  ElementRef,
  ViewChild,
  model
} from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { TableModule } from '@openng/optimus-ui/table';
import { ModelElevationProduct } from '../../models/model-elevation-product.model';
import { SvgIconsComponent, NgbModalService } from '@zayan-erp/shared';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ProductService } from '../../services/product.service';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { SellingPriceHistoryFormComponent } from '../selling-price-history-form/selling-price-history-form.component';
import { rxResourceFixed } from '@zayan-erp/shared';
import { SellingPriceHistory, SellingPriceHistoryService } from '../../services/selling-price-history.service';
import { of } from 'rxjs';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'lib-product-takeoff-list',
  imports: [
    CommonModule,
    TableModule,
    NgClass,
    SvgIconsComponent,
    ReactiveFormsModule,
    FormsModule,
    DatePickerModule,
    NgbTooltip,
    SellingPriceHistoryFormComponent,
    DatePipe
  ],
  templateUrl: './product-takeoff-list.component.html',
  styleUrl: './product-takeoff-list.component.scss'
})
export class ProductTakeoffListComponent {
  #modalService = inject(NgbModal);
  #ngbModalService = inject(NgbModalService);
  #formBuilder = inject(FormBuilder);
  #productService = inject(ProductService);
  #sellingPriceHistoryService = inject(SellingPriceHistoryService);
  currencySymbol = input<any | null>(null);
  productId = input<string>('');
  productCode = input<string>('');
  productName = input<string>('');
  items = input<ModelElevationProduct[]>([]);

  editClick = output<ModelElevationProduct>();
  removeClick = output<{ item: ModelElevationProduct; event: Event }>();
  editLinkedProductClick = output<{ parentItem: ModelElevationProduct; linkedProduct: ModelElevationProduct }>();
  linkedProductUpdated = output<void>();
  addNew = signal(false);
  newFieldData = model<SellingPriceHistory | null>(null);
  editingSellingPriceHistory = model<SellingPriceHistory | null>(null);
  selectedModelElevationId = model<string | null>(null);
  @ViewChild('sellingPriceModal') sellingPriceModal!: ElementRef;
  expandedRows: { [key: string]: boolean } = {};
  selectedLinkedProduct = signal<ModelElevationProduct | null>(null);
  quantityForm: FormGroup = this.#formBuilder.group({
    quantity: [0, [Validators.required, Validators.min(0)]]
  });
  sellingPriceHistory = rxResourceFixed({
    params: () => ({ modelElevationProductId: this.selectedModelElevationId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.modelElevationProductId) {
        return this.#sellingPriceHistoryService.readSellingPriceHistory(params.modelElevationProductId);
      }
      return of(null);
    }
  });

  editLinkedProductModal = viewChild<TemplateRef<any>>('editLinkedProductModal');

  hasLinkedProducts = (item: ModelElevationProduct): boolean => {
    return !!(item.linkedProducts && item.linkedProducts.length > 0);
  };

  onRowExpand(event: any) {
    // Row expansion is handled by PrimeNG automatically
  }

  onEdit(item: ModelElevationProduct) {
    this.editClick.emit(item);
  }

  onRemove(item: ModelElevationProduct, event: Event) {
    this.removeClick.emit({ item, event });
  }

  onEditLinkedProduct(parentItem: ModelElevationProduct, linkedProduct: ModelElevationProduct) {
    this.selectedLinkedProduct.set(linkedProduct);
    this.quantityForm.patchValue({
      quantity: linkedProduct.quantity ?? 0
    });

    const template = this.editLinkedProductModal();
    if (template) {
      this.#modalService.open(template, {
        ...this.#ngbModalService.centerModal,
        size: 'md'
      });
    }
  }

  onCancelEditLinkedProduct() {
    this.#modalService.dismissAll();
    this.selectedLinkedProduct.set(null);
    this.quantityForm.reset();
  }

  onSaveLinkedProductQuantity() {
    if (this.quantityForm.invalid) {
      this.quantityForm.markAllAsTouched();
      return;
    }

    const linkedProduct = this.selectedLinkedProduct();
    if (!linkedProduct || !linkedProduct.id) {
      return;
    }

    const quantity = this.quantityForm.get('quantity')?.value;
    const payload = {
      quantity: quantity
    };

    this.#productService.updateModelElevationProduct(linkedProduct.id, payload).subscribe({
      next: () => {
        this.#modalService.dismissAll();
        this.selectedLinkedProduct.set(null);
        this.quantityForm.reset();
        this.linkedProductUpdated.emit();
      },
      error: err => {
        console.error('Error updating linked product quantity:', err);
      }
    });
  }

  onSellingPrice(modelElevationProductId: string) {
    this.selectedModelElevationId.set(modelElevationProductId);
    this.#modalService.open(this.sellingPriceModal, {
      ...this.#ngbModalService.centerModal,
      size: 'xl'
    });
  }

  newFieldDetails(event: any) {
    this.newFieldData.set(event);
  }
}
